import React, { useContext, useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useEmptyCallback } from 'utilities/hooks.util';
import {
  Button,
  DataTable,
  DataTableSkeleton,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableContainer,
  TableCell,
  Toggle,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch,
  Pagination,
  PaginationSkeleton,
  DataTableCustomRenderProps
} from 'carbon-components-react';
import {
  Add16,
  CloseOutline16,
  CheckmarkOutline16,
  Error16
} from '@carbon/icons-react';
import Util from 'utilities/helper.util';
import { APP_URL } from 'types';
import {
  LocalStoreContext,
  LocalDispatchContext
} from '../../localStore/context';
import { getOrganisationsStart } from '../../localStore/actions';

const PageSizes = [10, 20, 50];

const OrganisationsList: React.FC<Props> = ({ onOpenNewForm, hide }) => {
  const history = useHistory();
  const location = useLocation<{ remain: boolean }>();
  const localStore = useContext(LocalStoreContext);
  const localDispatch = useContext(LocalDispatchContext);
  const [pageSize, setPageSize] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageOffset, setPageOffset] = useState<number>(0);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [filteredOrgsList, setFilteredOrgsList] = useState<any[]>([]);
  const [showDisabled, setShowDisabled] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const { organisations, getOrgsStatus, updateMultiOrgsStatus, reloadTrigger } =
    localStore;

  const handlePageChange = ({
    page,
    pageSize
  }: {
    page: number;
    pageSize: number;
  }) => {
    setPageOffset((page - 1) * pageSize);
    setCurrentPage(page);
    setPageSize(pageSize);
  };

  const getOrgsData = useEmptyCallback((queryParams: any) => {
    localDispatch(getOrganisationsStart(queryParams));
  });

  const debouncedFunction = Util.debounce(async (args: string[]) => {
    const text = args[0];
    setSearchText(text);
    setPageOffset(0);
    setCurrentPage(1);
  }, 250);

  useEffect(() => {
    const filterArr = [];
    if (searchText) {
      filterArr.push(`name:%${searchText}%`);
    }
    if (!showDisabled) {
      filterArr.push('is_inactive:false');
    }
    const queryParams = {
      filter: filterArr.length > 0 ? filterArr.join(',') : undefined,
      fields:
        'id,organisation_name,has_create_enquiry,organisation_group_name,master_admin_name,is_inactive',
      limit: pageSize,
      order: 'organisation_name',
      offset: pageOffset
    };
    getOrgsData(queryParams);
  }, [
    pageSize,
    pageOffset,
    getOrgsData,
    showDisabled,
    searchText,
    reloadTrigger
  ]);

  useEffect(() => {
    if (organisations) {
      setTotalItems(organisations.count);
      const filteredList = organisations.data?.map((org: any) => {
        const listObj = {
          id: org.id?.toString(),
          name: org.organisation_name,
          groupName:
            org.OrganisationGroupSubscribers?.organisation_group_id === 1
              ? 'EA'
              : 'BYDA',
          hasCreateEnquiry: !!org.has_create_enquiry,
          masterAdminName: org.master_admin_name,
          isInactive: !!org.is_inactive,
          subscriptionStart: org.OrganisationGroupSubscribers
            ?.subscription_start
            ? Util.getNewDateWithTimezone(
                org.OrganisationGroupSubscribers.subscription_start,
                'Australia/Melbourne'
              ).format('DD/MM/YYYY')
            : '',
          subscriptionEnd: org.OrganisationGroupSubscribers?.subscription_end
            ? Util.getNewDateWithTimezone(
                org.OrganisationGroupSubscribers.subscription_end,
                'Australia/Melbourne'
              ).format('DD/MM/YYYY')
            : '',
          contractRenewal: org.OrganisationGroupSubscribers?.contract_renewal
            ? Util.getNewDateWithTimezone(
                org.OrganisationGroupSubscribers.contract_renewal,
                'Australia/Melbourne'
              ).format('DD/MM/YYYY')
            : ''
        };
        return listObj;
      });
      setFilteredOrgsList(filteredList);
    }
  }, [organisations, getOrgsStatus]);

  useEffect(() => {
    updateMultiOrgsStatus === 'updating' && setSelectedIds([]);
  }, [updateMultiOrgsStatus]);

  useEffect(() => {
    if (location.pathname === APP_URL.SYSADMIN_ORG && !location.state?.remain) {
      setPageOffset(0);
      setCurrentPage(1);
    }
  }, [location]);

  const headers = [
    { key: 'isInactive', header: '' },
    { key: 'name', header: 'Organisation name' },
    { key: 'masterAdminName', header: 'Master admin' },
    { key: 'subscriptionStart', header: 'Subscription start date' },
    { key: 'subscriptionEnd', header: 'Subscription end date' },
    { key: 'contractRenewal', header: 'Final contractual end date' },
    { key: 'groupName', header: 'Organisation group' },
    { key: 'hasCreateEnquiry', header: 'BYDA Premium Features' }
  ];

  const activeCell = (
    <div className="premium-features-status active">
      <div className="icon-container">
        <CheckmarkOutline16 />
      </div>
      <div>Active</div>
    </div>
  );

  const inactiveCell = (
    <div className="premium-features-status inactive">
      <div className="icon-container">
        <CloseOutline16 />
      </div>
      <div>Inactive</div>
    </div>
  );

  const suspendedCell = (
    <div className="premium-features-status inactive">
      <div className="icon-container">
        <Error16 />
      </div>
    </div>
  );

  return (
    <div
      id="SysAdminOrgList"
      data-testid="SysAdminOrgList"
      className={`sys-admin-org-list${hide ? ' hidden' : ''}`}
    >
      <div className="page-title-container">
        <div className="page-title">Organisations</div>
        <div className="toggle">
          <Toggle
            id={'toggleShowDisabled'}
            aria-label="Show suspended"
            labelA="Show suspended"
            labelB="Show suspended"
            checked={showDisabled}
            onToggle={(show) => setShowDisabled(show)}
          />
        </div>
      </div>
      <DataTable rows={filteredOrgsList} headers={headers}>
        {({
          rows,
          getTableProps,
          getRowProps,
          getHeaderProps,
          getToolbarProps
        }: DataTableCustomRenderProps) => {
          return (
            <TableContainer>
              <TableToolbar
                {...getToolbarProps()}
                aria-label="data table toolbar"
              >
                <TableToolbarContent>
                  <TableToolbarSearch
                    onChange={(e) =>
                      debouncedFunction(e.currentTarget?.value || '')
                    }
                    persistent
                    placeholder="Search organisation"
                    defaultValue={searchText}
                  />
                  <Button onClick={() => onOpenNewForm()} renderIcon={Add16}>
                    Add organisation
                  </Button>
                </TableToolbarContent>
              </TableToolbar>
              {getOrgsStatus !== 'fetching' &&
              updateMultiOrgsStatus !== 'updating' ? (
                <Table {...getTableProps()}>
                  <TableHead>
                    <TableRow>
                      {headers.map((header, i) => (
                        <TableHeader {...getHeaderProps({ header })} key={i}>
                          {header.header}
                        </TableHeader>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <>
                      {rows.length > 0 ? (
                        <>
                          {rows.map((row: any) => {
                            const cells = row.cells;
                            const c_0 = cells[0],
                              c_1 = cells[1],
                              c_2 = cells[2],
                              c_3 = cells[3],
                              c_4 = cells[4],
                              c_5 = cells[5],
                              c_6 = cells[6],
                              c_7 = cells[7];
                            row.isSelected = selectedIds.includes(
                              Number(row.id)
                            );
                            return (
                              <TableRow
                                {...getRowProps({ row })}
                                key={row.id}
                                onClick={() =>
                                  history.push(
                                    `${APP_URL.SYSADMIN_ORG}/${row.id}`
                                  )
                                }
                              >
                                <TableCell key={c_0.id} />
                                <TableCell key={c_1.id}>
                                  <div className="data-table-org-name-cell">
                                    {c_0.value ? suspendedCell : ''} {c_1.value}
                                  </div>
                                </TableCell>
                                <TableCell key={c_2.id}>{c_2.value}</TableCell>
                                <TableCell key={c_3.id}>{c_3.value}</TableCell>
                                <TableCell key={c_5.id}>{c_5.value}</TableCell>
                                <TableCell key={c_4.id}>{c_4.value}</TableCell>
                                <TableCell key={c_6.id}>{c_6.value}</TableCell>
                                <TableCell key={c_7.id}>
                                  {c_7.value ? activeCell : inactiveCell}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </>
                      ) : (
                        <TableRow>
                          <TableCell colSpan={7}>No Record</TableCell>
                        </TableRow>
                      )}
                    </>
                  </TableBody>
                </Table>
              ) : (
                <DataTableSkeleton
                  headers={headers}
                  rowCount={3}
                  showHeader={false}
                  showToolbar={false}
                />
              )}
            </TableContainer>
          );
        }}
      </DataTable>
      {getOrgsStatus !== 'fetching' && updateMultiOrgsStatus !== 'updating' ? (
        <Pagination
          size="lg"
          onChange={(data) => handlePageChange(data)}
          backwardText="Previous page"
          forwardText="Next page"
          itemsPerPageText="Display:"
          page={currentPage}
          pageNumberText="Page Number"
          pageSize={pageSize}
          pageSizes={PageSizes}
          totalItems={totalItems}
        />
      ) : (
        <PaginationSkeleton />
      )}
    </div>
  );
};

type Props = {
  onOpenNewForm: () => void;
  hide?: boolean;
};

export default React.memo(OrganisationsList);
